import { useContext, useEffect, useReducer, useState, useMemo } from "react";
import { isAdminProfile } from "../utils/isAdminProfile";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import { isArray } from "lodash";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ForumIcon from "@mui/icons-material/Forum";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import Description from "@mui/icons-material/Description";
import DvrOutlinedIcon from "@mui/icons-material/DvrOutlined";
import Schedule from "@mui/icons-material/Schedule";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import PeopleIcon from "@mui/icons-material/People";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AttachFile from "@mui/icons-material/AttachFile";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DeviceHubOutlined from "@mui/icons-material/DeviceHubOutlined";
import AllInclusive from "@mui/icons-material/AllInclusive";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CakeIcon from "@mui/icons-material/Cake";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import PhonelinkSetup from "@mui/icons-material/PhonelinkSetup";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import WhatsAppIconMui from "@mui/icons-material/WhatsApp";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "./usePlans";
import useHelps from "./useHelps";
import { i18n } from "../translate/i18n";
import useCompanySettings from "./useSettings/companySettings";

const chatsReducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const newChats = [];
    if (isArray(action.payload)) {
      action.payload.forEach((chat) => {
        const idx = state.findIndex((u) => u.id === chat.id);
        if (idx !== -1) state[idx] = chat; else newChats.push(chat);
      });
    }
    return [...state, ...newChats];
  }
  if (action.type === "CHANGE_CHAT") {
    return state.map((chat) => chat.id === action.payload.chat.id ? action.payload.chat : chat);
  }
  return state;
};

// RDS-NAVGROUPS: fuente unica de verdad para la navegacion (sidebar desktop en
// MainListItems.js y el bottom-sheet "Mas" en layout/index.js). Cada item incluye
// `sub` (subtitulo descriptivo) y cada grupo incluye `color`/`bg` -- el sidebar
// desktop simplemente no renderiza esos campos, el menu movil si.
const useNavGroups = () => {
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showWallets, setShowWallets] = useState(false);
  const [hasHelps, setHasHelps] = useState(false);
  const [invisible, setInvisible] = useState(true);
  const [pageNumber] = useState(1);
  const [chats, dispatch] = useReducer(chatsReducer, []);

  const { list: listHelps } = useHelps();
  const { get: getSetting } = useCompanySettings();
  const { getPlanCompany } = usePlans();

  useEffect(() => { listHelps().then(h => setHasHelps(h.length > 0)); }, []);

  useEffect(() => {
    getSetting({ column: "DirectTicketsToWallets" })
      .then(s => setShowWallets(s.DirectTicketsToWallets))
      .catch(toastError);
  }, []);

  useEffect(() => {
    if (!user?.companyId) return;
    getPlanCompany(undefined, user.companyId)
      .then(p => {
        setShowCampaigns(p.plan.useCampaigns);
        setShowKanban(p.plan.useKanban);
        setShowOpenAi(p.plan.useOpenAi);
        setShowIntegrations(p.plan.useIntegrations);
        setShowSchedules(p.plan.useSchedules);
        setShowInternalChat(p.plan.useInternalChat);
        setShowExternalApi(p.plan.useExternalApi);
      })
      .catch(toastError);
  }, [user?.companyId]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/chats/", { params: { searchParam: "", pageNumber } })
        .then(r => dispatch({ type: "LOAD_CHATS", payload: r.data.records }))
        .catch(toastError);
    }, 500);
    return () => clearTimeout(t);
  }, [pageNumber]);

  useEffect(() => {
    if (!user.id || !socket || typeof socket.on !== "function") return;
    const eventName = `company-${user.companyId}-chat`;
    const handler = (data) => {
      if (data.action === "new-message" || data.action === "update")
        dispatch({ type: "CHANGE_CHAT", payload: data });
    };
    socket.on(eventName, handler);
    return () => socket.off?.(eventName, handler);
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreads = 0;
    chats.forEach(c => c.users?.forEach(cu => { if (cu.userId === user.id) unreads += cu.unreads; }));
    setInvisible(unreads === 0);
  }, [chats, user.id]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (whatsApps.length > 0) {
        setConnectionWarning(whatsApps.some(w =>
          ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
        ));
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  const isAdmin = isAdminProfile(user.profile) || user.allowConnections === "enabled" || user.showDashboard === "enabled";
  const chatBadge = invisible ? 0 : chats.reduce((acc, c) => {
    c.users?.forEach(cu => { if (cu.userId === user.id) acc += cu.unreads; });
    return acc;
  }, 0);

  const navGroups = useMemo(() => [
    {
      id: "atencion", label: "Atención", color: "#0399FF", bg: "#eff6ff",
      items: [
        { to: "/tickets", icon: <WhatsAppIconMui />, label: i18n.t("mainDrawer.listItems.tickets"), sub: "Conversaciones activas" },
        ...(showKanban ? [{ to: "/kanban", icon: <ViewColumnIcon />, label: i18n.t("mainDrawer.listItems.kanban"), sub: "Vista de tablero" }] : []),
        ...(showInternalChat ? [{ to: "/chats", icon: <ForumIcon />, label: i18n.t("mainDrawer.listItems.chats"), badge: chatBadge, badgeColor: "red", sub: "Comunicación del equipo" }] : []),
        ...(user.showContacts === "enabled" ? [{ to: "/contacts", icon: <ContactPhoneOutlinedIcon />, label: i18n.t("mainDrawer.listItems.contacts"), sub: "Clientes y prospectos" }] : []),
      ],
    },
    {
      id: "llamadas_ia", label: "Llamadas IA", color: "#0891b2", bg: "#ecfeff",
      items: [
        ...(isAdmin ? [{ to: "/llamadas-ia", icon: <PhoneInTalkIcon />, label: "Llamadas IA", sub: "Asistentes de voz con IA" }] : []),
      ],
    },
    {
      id: "analisis", label: "Análisis", color: "#16a34a", bg: "#f0fdf4",
      items: [
        ...(isAdmin ? [
          { to: "/", icon: <DashboardOutlinedIcon />, label: "Dashboard", sub: "Métricas en tiempo real" },
          { to: "/reports", icon: <Description />, label: i18n.t("mainDrawer.listItems.reports"), sub: "Historial y exportación" },
          { to: "/moments", icon: <DvrOutlinedIcon />, label: i18n.t("mainDrawer.listItems.chatsTempoReal"), sub: "Momentos del servicio" },
        ] : []),
        ...(showSchedules ? [{ to: "/schedules", icon: <Schedule />, label: i18n.t("mainDrawer.listItems.schedules"), sub: "Horarios de atención" }] : []),
        { to: "/tags", icon: <LocalOfferIcon />, label: i18n.t("mainDrawer.listItems.tags"), sub: "Etiquetas de tickets" },
        { to: "/quick-messages", icon: <FlashOnIcon />, label: i18n.t("mainDrawer.listItems.quickMessages"), sub: "Plantillas de mensaje" },
      ],
    },
    {
      id: "automatizacion", label: "Automatización", color: "#d97706", bg: "#fffbeb",
      items: [
        ...(showCampaigns && user.showCampaign === "enabled" ? [
          { to: "/campaigns", icon: <RecordVoiceOverIcon />, label: i18n.t("mainDrawer.listItems.campaigns"), sub: "Mensajes masivos" },
          { to: "/contact-lists", icon: <PeopleIcon />, label: i18n.t("campaigns.subMenus.listContacts"), sub: "Segmentación" },
          { to: "/campaigns-config", icon: <SettingsOutlinedIcon />, label: i18n.t("campaigns.subMenus.settings"), sub: "Ajustes de campañas" },
        ] : []),
        ...(user.showFlow === "enabled" ? [
          { to: "/flowbuilders", icon: <TimelineIcon />, label: "Flujo de conversación", sub: "FlowBuilder visual" },
          { to: "/phrase-lists", icon: <EventAvailableIcon />, label: "Frases automáticas", sub: "Respuestas automatizadas" },
        ] : []),
        ...(isAdmin ? [{ to: "/files", icon: <AttachFile />, label: i18n.t("mainDrawer.listItems.files"), sub: "Documentos compartidos" }] : []),
      ],
    },
    {
      id: "admin", label: "Administración", color: "#7c3aed", bg: "#f5f3ff",
      items: [
        ...(isAdmin ? [
          { to: "/connections", icon: <SyncAltIcon />, label: i18n.t("mainDrawer.listItems.connections"), badge: connectionWarning ? 1 : 0, badgeColor: "amber", sub: "WhatsApp, Facebook, Instagram" },
          { to: "/users", icon: <PeopleAltOutlinedIcon />, label: i18n.t("mainDrawer.listItems.users"), sub: "Agentes y permisos" },
          { to: "/queues", icon: <AccountTreeOutlinedIcon />, label: i18n.t("mainDrawer.listItems.queues"), sub: "Equipos de trabajo" },
          ...(isAdminProfile(user.profile) ? [{ to: "/custom-fields", icon: <ListOutlinedIcon />, label: "Campos personalizados", sub: "Datos de contacto a medida" }] : []),
          ...(isAdminProfile(user.profile) ? [{ to: "/flujos", icon: <ListOutlinedIcon />, label: "Flujos", sub: "Bases de datos a medida" }] : []),
          ...(showIntegrations ? [{ to: "/queue-integration", icon: <DeviceHubOutlined />, label: i18n.t("mainDrawer.listItems.queueIntegration"), sub: "APIs y bots" }] : []),
          ...(showOpenAi ? [{ to: "/prompts", icon: <AllInclusive />, label: i18n.t("mainDrawer.listItems.prompts"), sub: "Asistente IA" }] : []),
          ...(isAdminProfile(user.profile) && showWallets ? [{ to: "/wallets", icon: <AccountBalanceWalletIcon />, label: i18n.t("mainDrawer.listItems.wallets"), sub: "Carteras de contactos" }] : []),
          { to: "/birthday-settings", icon: <CakeIcon />, label: i18n.t("mainDrawer.listItems.birthdaySettings"), sub: "Mensajes automáticos" },
        ] : []),
        ...(user.super ? [
          { to: "/announcements", icon: <AnnouncementIcon />, label: i18n.t("mainDrawer.listItems.annoucements"), sub: "Boletines internos" },
          { to: "/allConnections", icon: <PhonelinkSetup />, label: i18n.t("mainDrawer.listItems.allConnections"), sub: "Vista global" },
          { to: "/companies", icon: <BusinessIcon />, label: i18n.t("mainDrawer.listItems.companies"), sub: "Multi-empresa" },
          { to: "/whatsapp-templates", icon: <DescriptionOutlinedIcon />, label: "Plantillas WhatsApp", sub: "Meta Cloud API" },
        ] : []),
      ],
    },
    {
      id: "sistema", label: "Sistema", color: "#6b7280", bg: "#f8f7f5",
      items: [
        ...(isAdmin ? [
          { to: "/settings", icon: <SettingsOutlinedIcon />, label: i18n.t("mainDrawer.listItems.settings"), sub: "Opciones del sistema" },
          { to: "/financeiro", icon: <LocalAtmIcon />, label: i18n.t("mainDrawer.listItems.financeiro"), sub: "Facturas y pagos" },
        ] : []),
        { to: "/notifications", icon: <NotificationsActiveOutlinedIcon />, label: "Notificaciones", sub: "Push y alertas" },
        ...(showExternalApi && isAdmin ? [{ to: "/messages-api", icon: <CodeRoundedIcon />, label: i18n.t("mainDrawer.listItems.messagesAPI"), sub: "Documentación" }] : []),
        ...(hasHelps ? [{ to: "/helps", icon: <HelpOutlineIcon />, label: i18n.t("mainDrawer.listItems.helps"), sub: "Guías y tutoriales" }] : []),
      ],
    },
  ], [showCampaigns, showKanban, showOpenAi, showIntegrations, showSchedules,
      showInternalChat, showExternalApi, showWallets, hasHelps, isAdmin,
      connectionWarning, chatBadge, user]);

  return { navGroups, isAdmin, chatBadge, connectionWarning };
};

export default useNavGroups;
